import React, { useState, useRef, useEffect } from 'react';
import './MultipleSelect.css';

const MultipleSelect = ({ options, value, onChange, placeholder, label, required, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsListRef = useRef(null);

  // Deshabilitar scroll del body cuando el dropdown está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Prevenir la propagación de eventos hacia abajo
  useEffect(() => {
    const handleDropdownClick = (e) => {
      e.stopPropagation();
    };

    const optionsList = optionsListRef.current;
    if (isOpen && optionsList) {
      optionsList.addEventListener('mousedown', handleDropdownClick);
      return () => {
        optionsList.removeEventListener('mousedown', handleDropdownClick);
      };
    }
  }, [isOpen]);

  // Filtrar opciones basado en el término de búsqueda
  const filteredOptions = options ? options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  const handleOptionClick = (option, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault(); // Prevenir comportamiento por defecto
    }
    
    const isSelected = value && value.some(item => item.value === option.value);
    let newValue;
    
    if (isSelected) {
      newValue = value.filter(item => item.value !== option.value);
    } else {
      newValue = [...(value || []), option];
    }
    
    onChange(newValue);
    
    // No cerramos el dropdown para permitir selecciones múltiples
    // Solo actualizamos la búsqueda
    setSearchTerm('');
    
    // Mantener el foco en el input de búsqueda
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSearchChange = (e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  };

  const handleSelectClick = (e) => {
    if (e) {
      e.stopPropagation(); // Detener la propagación aquí también
      e.preventDefault(); // Prevenir el comportamiento por defecto
    }
    
    setIsOpen(!isOpen);
    
    // Si estamos abriendo el select, esperamos un momento para que el input esté disponible
    if (!isOpen) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 10);
    }
  };

  // Manejar clic en el overlay para cerrar el dropdown
  const handleOverlayClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="multiple-select-container">
      {label && (
        <label className="multiple-select-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      <div 
        className={`multiple-select ${isOpen ? 'is-open' : ''} ${error ? 'invalid' : ''}`} 
        ref={dropdownRef}
        tabIndex="0"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectClick(e);
          }
        }}
      >
        <div 
          className={`select-input ${isOpen ? 'open' : ''}`}
          onClick={handleSelectClick}
        >
          <div className="selected-options">
            {(!value || value.length === 0) && (
              <span className="placeholder">{placeholder}</span>
            )}
            {value && value.map(option => (
              <span key={option.value} className="selected-tag">
                {option.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleOptionClick(option, e);
                  }}
                  className="remove-tag"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <span className="arrow-icon">▼</span>
        </div>

        {isOpen && (
          <>
            {/* Overlay transparente para capturar clics fuera del dropdown */}
            <div 
              className="dropdown-overlay" 
              onClick={handleOverlayClick}
              onTouchStart={handleOverlayClick}
            />
            
            <div 
              className="dropdown"
              role="listbox"
              ref={optionsListRef}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              />
              <div 
                className="options-list"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {filteredOptions.map(option => (
                  <div
                    key={option.value}
                    className={`option ${value && value.some(item => item.value === option.value) ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleOptionClick(option, e);
                    }}
                  >
                    {option.label}
                  </div>
                ))}
                {filteredOptions.length === 0 && (
                  <div className="no-results">No se encontraron resultados</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="field-feedback">
        {error ? <span className="field-error">{error}</span> : <span className="field-error" style={{ visibility: 'hidden' }}>&nbsp;</span>}
      </div>
    </div>
  );
};

export default MultipleSelect; 